import catalogsClient from "@/axiosClients/catalogsClient.js";
import questionnairesClient from "@/axiosClients/questionnairesClient.js";
import authClient from "@/axiosClients/authClient.js";
import metricsClient from "@/axiosClients/metricsClient";
import { errorHttpMessage } from "@/utils/errorDecodeUtil";
import psychologistService from '@/services/psychologistService'
import usersService from "@/services/usersService";
import {
  unlockMasterKeyWithPin,
  unlockMasterKeyWithRecoveryCode
} from '@/modules/psychology/clinical-notes/crypto/clinicalNotesCrypto'
import {
  persistClinicalNotesMasterKey as persistMasterKeyToLocalStorage,
  loadClinicalNotesMasterKey
} from '@/modules/psychology/clinical-notes/storage/masterKeyLocalStorage'
import { PSYCHOLOGIST_ROLE_ID } from '@/config/constants'

/**
 * Carga el estado E2E de notas clínicas solo para psicólogos.
 */
export const fetchClinicalNotesCryptoStatusIfPsychologist = async ({ commit, dispatch, state }) => {
  const user = state.auth?.user
  if (!user || Number(user.role_id) !== PSYCHOLOGIST_ROLE_ID) {
    commit('setClinicalNotesCryptoStatus', null)
    return { skipped: true }
  }

  const { data, error, message } = await psychologistService.getClinicalNotesCryptoStatus()

  if (!error) {
    commit('setClinicalNotesCryptoStatus', data ?? null)
    if (data?.pin_configured) {
      await dispatch('tryHydrateClinicalNotesMasterKeyFromStorage')
    }
    return { data: data ?? null, error: false, message }
  }

  commit('setClinicalNotesCryptoStatus', null)
  return { data: null, error: true, message }
}

/** POST clinical-notes/crypto/envelopes. */
export const enrollClinicalNotesCrypto = async (_ctx, enrollmentPayload) => {
  const { data, error, message } = await psychologistService.postClinicalNotesCryptoEnvelopes(enrollmentPayload)
  return { data, error, message }
}

export const fetchClinicalNotesCryptoEnvelopesIfPsychologist = async ({ state }) => {
  const user = state.auth?.user
  if (!user || Number(user.role_id) !== PSYCHOLOGIST_ROLE_ID) {
    return { data: null, error: false, message: null, skipped: true }
  }
  return psychologistService.getClinicalNotesCryptoEnvelopes()
}

/** Tras mostrar el código de recuperación, alinea el store con el servidor ya enrollado. */
export const markClinicalNotesCryptoPinConfigured = ({ commit, state }) => {
  const prev = state.clinicalNotesCryptoStatus && typeof state.clinicalNotesCryptoStatus === 'object'
    ? { ...state.clinicalNotesCryptoStatus }
    : {}
  commit('setClinicalNotesCryptoStatus', { ...prev, pin_configured: true })
}

export const persistClinicalNotesMasterKey = ({ commit, state }, { masterKeyBytes }) => {
  const user = state.auth?.user
  if (!user || Number(user.role_id) !== PSYCHOLOGIST_ROLE_ID || !masterKeyBytes?.length) {
    return { ok: false }
  }
  const ok = persistMasterKeyToLocalStorage(masterKeyBytes)
  if (ok) {
    commit('setClinicalNotesMasterKeyBytes', new Uint8Array(masterKeyBytes))
  }
  return { ok }
}

export const tryHydrateClinicalNotesMasterKeyFromStorage = ({ commit, state }) => {
  const user = state.auth?.user
  if (!user || Number(user.role_id) !== PSYCHOLOGIST_ROLE_ID) {
    return { ok: false, skipped: true }
  }
  const bytes = loadClinicalNotesMasterKey()
  if (!bytes) return { ok: false }
  commit('setClinicalNotesMasterKeyBytes', bytes)
  return { ok: true }
}

export const unlockClinicalNotesMasterKeyWithPin = async ({ dispatch, commit }, { pin }) => {
  const fetchResult = await dispatch('fetchClinicalNotesCryptoEnvelopesIfPsychologist')
  if (fetchResult.skipped) return { ok: false, message: fetchResult.message }
  if (fetchResult.error || !fetchResult.data) {
    return { ok: false, message: fetchResult.message }
  }

  try {
    const masterKeyBytes = await unlockMasterKeyWithPin(pin, fetchResult.data)
    const persisted = persistMasterKeyToLocalStorage(masterKeyBytes)
    if (!persisted) {
      return { ok: false, message: 'No se pudo guardar en el navegador' }
    }
    commit('setClinicalNotesMasterKeyBytes', new Uint8Array(masterKeyBytes))
    return { ok: true }
  } catch (e) {
    return { ok: false, message: e?.message || 'Error al desbloquear' }
  }
}

export const unlockClinicalNotesMasterKeyWithRecoveryCode = async ({ dispatch, commit }, { recoveryCode }) => {
  const fetchResult = await dispatch('fetchClinicalNotesCryptoEnvelopesIfPsychologist')
  if (fetchResult.skipped) return { ok: false, message: fetchResult.message }
  if (fetchResult.error || !fetchResult.data) {
    return { ok: false, message: fetchResult.message }
  }

  try {
    const masterKeyBytes = await unlockMasterKeyWithRecoveryCode(recoveryCode, fetchResult.data)
    const persisted = persistMasterKeyToLocalStorage(masterKeyBytes)
    if (!persisted) {
      return { ok: false, message: 'No se pudo guardar en el navegador' }
    }
    commit('setClinicalNotesMasterKeyBytes', new Uint8Array(masterKeyBytes))
    return { ok: true }
  } catch (e) {
    return { ok: false, message: e?.message || 'Error al desbloquear' }
  }
}

export const getCatalogs = async ({ commit }, params = {}) => {
  try {
    const { data: result } = await catalogsClient.get(`/reason-consultation`);
    return result.result;
  } catch (ex) {
    const error_http_message = errorHttpMessage(ex);
    return {
      message: error_http_message.message,
      error_keys: error_http_message.error_keys,
      data: ex.response.data,
      error: true,
    };
  }
};

export const getQuestionnaires = async ({ commit }) => {
  try {
    const { data: result } = await questionnairesClient.get(`/initial`);
    const { message, error } = result;
    return { data: result.result.data.questions, message, error };
  } catch (ex) {
    const error_http_message = errorHttpMessage(ex);
    return {
      message: error_http_message.message,
      error_keys: error_http_message.error_keys,
      data: ex.response.data,
      error: true,
    };
  }
};

export const registerUser = async ({ commit }, user) => {
  try {
    const { data: result } = await authClient.post(`/register`, user);
    const { message, error, data } = result.result;
    if (!error) {
      const { user, expires_in } = data;
      commit("login", { user, expires_in });
    }
    return { message, error };
  } catch (ex) {
    const error_http_message = errorHttpMessage(ex);
    return {
      message: error_http_message.message,
      error_keys: error_http_message.error_keys,
      data: ex.response.data,
      error: true,
    };
  }
};

export const registerB2bUser = async ({ commit, dispatch }, b2bUser) => {
  try {
    const endpoint = b2bUser.isFamily ? '/b2b/register/family' : '/b2b/register';
    const { data: result } = await authClient.post(endpoint, b2bUser);
    const { message, error, data } = result.result;

    if (!error) {
      const { user, expires_in } = data;
      commit("login", { user, expires_in });
      await dispatch('fetchClinicalNotesCryptoStatusIfPsychologist')
    }

    return { data, message, error };

  } catch (ex) {
    const error_http_message = errorHttpMessage(ex);
    return {
      message: error_http_message?.message,
      error_keys: error_http_message?.error_keys,
      data: ex.response.data,
      error: true,
    };
  }
};

export const loginUser = async ({ commit, dispatch }, { password, email_or_nomina }) => {
  try {
    const { data: result } = await authClient.post("/login", {
      password,
      email_or_nomina,
    });
    const { error, message } = result.result;
    const data = result.result.data.user || null;
    if (!error) {
      const { expires_in, user } = result.result.data;
      commit("login", { user, expires_in });
      await dispatch('fetchClinicalNotesCryptoStatusIfPsychologist')
    }
    return { data, error, message };
  } catch (ex) {
    const error_http_message = errorHttpMessage(ex);
    return {
      message: error_http_message.message,
      error_keys: error_http_message.error_keys,
      // data: ex.response.data,
      error: true,
    };
  }
};

export const sendCodeUser = async ({ commit }, recoverPassData) => {
  try {
    const { data } = await authClient.post(`/reset-password/code`, recoverPassData);
    return data.result;
  } catch (ex) {
    const error_http_message = errorHttpMessage(ex);
    return {
      message: error_http_message.message,
      error_keys: error_http_message.error_keys,
      data: ex.response.data,
      error: true,
    };
  }
};

export const resetPass = async ({ commit }, newPassData) => {
  try {
    const { data } = await authClient.post(`/reset-password`, newPassData);
    return data.result;
  } catch (ex) {
    const error_http_message = errorHttpMessage(ex);
    return {
      message: error_http_message.message,
      error_keys: error_http_message.error_keys,
      data: ex.response.data,
      error: true,
    };
  }
};

export const saveInterested = async ({ commit }, params) => {
  try {
    const { data } = await psychologistService.saveInterested(params);
    return data.result;
  } catch (ex) {
    const error_http_message = errorHttpMessage(ex);
    return {
      message: error_http_message.message,
      error_keys: error_http_message.error_keys,
      data: ex.response.data,
      error: true,
    };
  }
};

export const refreshToken = async ({ commit }) => {
  try {
    const { data: result } = await authClient.post('/refresh')
    const { data, message, error } = result.result

    if (!error && data?.expires_in != null) {
      commit('updateToken', { expiresIn: data.expires_in })
    }

    return { message, error }
  } catch (ex) {
    const error_http_message = errorHttpMessage(ex)
    return { message: error_http_message.message, data: ex, error: true }
  }
}

/**
 * Obtiene la sesión actual (cookie).
 * No guarda en store: cuando los guards redirigen a verify-token, VerifyToken.vue hace el commit.
 */
export const getSession = async () => {
  try {
    const { data: result } = await authClient.get('/session')
    const { data, message, error } = result?.result ?? {}

    return { data: result?.result?.data ?? result, message, error }
  } catch (ex) {
    const error_http_message = errorHttpMessage(ex)
    return { message: error_http_message.message, data: ex, error: true }
  }
}

export const saveConsultReasonsToUser = async ({ commit }, data) => {

  try {
    const { data: result } = await authClient.post('/user/consult-reasons', data)
    const { message, error } = result.result

    if (!error) {
      commit('clearReasonsPartnerAndAnwers')
    }

    return { message, error }

  } catch (ex) {
    const error_http_message = errorHttpMessage(ex);
    return { message: error_http_message.message, data: ex, error: true }
  }
}

export const sendSecurityQuestions = async ({ commit }, securityQuestionsData) => {
  try {
    const { data: result } = await authClient.post('/validate-security-question', securityQuestionsData)
    const { data, message, error } = result.result
    return { data, message, error }

  } catch (ex) {
    const error_http_message = errorHttpMessage(ex);
    return { message: error_http_message.message, data: ex, error: true }
  }
}

export const resetB2bPass = async ({ commit }, newPassData) => {
  try {
    const { data: result } = await authClient.post('/reset-password/number-nomina', newPassData)
    const { data, message, error } = result.result
    return { data, message, error }

  } catch (ex) {
    const error_http_message = errorHttpMessage(ex);
    return { message: error_http_message.message, data: ex, error: true }
  }
}

export const verifyToken = async ({ commit }, { expires_in, origin = "mimente" }) => {
  try {
    const { data: result } = await authClient.post('/verify-token', { expires_in, origin })

    return {
      data: result.result?.data || result.result,
      error: result.result?.error || false,
      message: result.result?.message || null
    }
  } catch (ex) {
    const error_http_message = errorHttpMessage(ex)
    return {
      message: error_http_message.message,
      error_keys: error_http_message.error_keys,
      error: true,
      status: ex.response?.status || null
    }
  }
};

/**
 * Cierra sesión solo en el cliente: limpia estado y storage sin llamar a la API.
 */
export const logoutLocalOnly = ({ commit }) => {
  commit('logout')
}

/**
 * Cierra sesión en el backend (invalida la cookie) y limpia el estado local.
 */
export const logout = async ({ commit }) => {
  try {
    sessionStorage.setItem('justLoggedOut', 'true')
    await authClient.post('/logout')
  } catch (_) {
    // Aun si falla, limpiamos el estado local
  }
  commit('logout')
}